import { parseCalendarDate, formatUtc } from "../utilities/dateTime";

export const PARTICIPANT_EARLIEST_DATE = "1900-01-01";

export const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getParticipantDateLimits = (today = new Date()) => {
  const cpocMaximum = new Date(today.getFullYear() + 2, today.getMonth(), today.getDate());
  return {
    minimum: PARTICIPANT_EARLIEST_DATE,
    historicalMaximum: formatLocalDate(today),
    cpocMaximum: formatLocalDate(cpocMaximum),
  };
};

type DateValue = Date | string | null | undefined;

export interface ParticipantDateValues {
  linkageDate?: DateValue;
  certificationDate?: DateValue;
  cpocDate?: DateValue;
  cpocEndDate?: DateValue;
}

const toDateOnly = (value: DateValue): string | null => {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return formatLocalDate(value);
  }

  return parseCalendarDate(value) ? value : null;
};

export const validateParticipantDates = (
  values: ParticipantDateValues,
  options: { linkageRequired?: boolean; today?: Date } = {},
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const limits = getParticipantDateLimits(options.today);
  const linkageDate = toDateOnly(values.linkageDate);
  const certificationDate = toDateOnly(values.certificationDate);
  const cpocDate = toDateOnly(values.cpocDate);
  const cpocEndDate = toDateOnly(values.cpocEndDate);

  if (options.linkageRequired && !values.linkageDate) {
    errors.linkageDate = "Linkage Date is required.";
  }

  const validateRange = (
    field: keyof ParticipantDateValues,
    label: string,
    rawValue: DateValue,
    normalizedValue: string | null,
    maximum: string,
  ) => {
    if (!rawValue) return;
    if (!normalizedValue) {
      errors[field] = `${label} must be a valid date.`;
    } else if (normalizedValue < limits.minimum) {
      errors[field] = `${label} cannot be before January 1, 1900.`;
    } else if (normalizedValue > maximum) {
      errors[field] = `${label} cannot be after ${formatUtc(parseCalendarDate(maximum)!, "MMMM d, yyyy")}.`;
    }
  };

  validateRange("linkageDate", "Linkage Date", values.linkageDate, linkageDate, limits.historicalMaximum);
  validateRange("certificationDate", "Certification Date", values.certificationDate, certificationDate, limits.historicalMaximum);
  validateRange("cpocDate", "CPOC Start Date", values.cpocDate, cpocDate, limits.cpocMaximum);
  validateRange("cpocEndDate", "CPOC End Date", values.cpocEndDate, cpocEndDate, limits.cpocMaximum);

  if (values.cpocDate && !values.cpocEndDate) {
    errors.cpocEndDate = "CPOC End Date is required when a Start Date is provided.";
  }
  if (values.cpocEndDate && !values.cpocDate) {
    errors.cpocDate = "CPOC Start Date is required when an End Date is provided.";
  }
  if (cpocDate && cpocEndDate && cpocEndDate < cpocDate) {
    errors.cpocEndDate = "CPOC End Date cannot be before the Start Date.";
  }

  return errors;
};

// Participant dates are calendar values, not UTC timestamps.
export const formatParticipantDate = (value: Date | string | null | undefined): string =>
  value instanceof Date ? (Number.isNaN(value.getTime()) ? "" : formatLocalDate(value)) : value || "";
